// Shared target geometry for command steering and combat range checks.

import { buildingDef } from '../content/registry';
import type { PlayerId } from '../content/types';
import { clamp, dist, type Vec2 } from '../shared/vec';
import type { Sim } from './sim';
import { footprintOf, type Building } from './state';

export function targetPosition(sim: Sim, id: number): Vec2 | null {
  const squad = sim.state.squads.get(id);
  if (squad) return squad.pos;
  const building = sim.state.buildings.get(id);
  if (building) return building.pos;
  const worker = sim.state.workers.get(id);
  if (worker) return worker.pos;
  const mine = sim.state.mines.get(id);
  if (mine) return mine.pos;
  return null;
}

export function targetOwner(sim: Sim, id: number): PlayerId | null {
  return (
    sim.state.squads.get(id)?.owner ??
    sim.state.buildings.get(id)?.owner ??
    sim.state.workers.get(id)?.owner ??
    sim.state.mines.get(id)?.owner ??
    null
  );
}

export function isHostileTarget(sim: Sim, owner: PlayerId, id: number): boolean {
  const target = targetOwner(sim, id);
  return target !== null && sim.areHostile(owner, target);
}

/** Exact distance from a world point to an axis-aligned, rotation-aware building footprint. */
export function distanceToBuildingFootprint(from: Vec2, building: Building): number {
  const fp = footprintOf(buildingDef(building.defId), building.rot);
  const dx = Math.max(0, Math.abs(from.x - building.pos.x) - fp.w / 2);
  const dy = Math.max(0, Math.abs(from.y - building.pos.y) - fp.h / 2);
  return Math.hypot(dx, dy);
}

/** Distance to the target's attackable surface (center distance for non-buildings). */
export function distanceToTarget(sim: Sim, from: Vec2, targetId: number): number | null {
  const building = sim.state.buildings.get(targetId);
  if (building) return distanceToBuildingFootprint(from, building);
  const pos = targetPosition(sim, targetId);
  return pos ? dist(from, pos) : null;
}

/**
 * Walkable stand-off point on the attacker's current side of a building. Steering at this
 * point avoids sending every attacker toward the blocked building center (and then relying
 * on nearest-cell scan order to choose an arbitrary side).
 */
export function buildingApproachPoint(from: Vec2, building: Building, weaponRange: number): Vec2 {
  const fp = footprintOf(buildingDef(building.defId), building.rot);
  const halfW = fp.w / 2;
  const halfH = fp.h / 2;
  const left = building.pos.x - halfW;
  const right = building.pos.x + halfW;
  const top = building.pos.y - halfH;
  const bottom = building.pos.y + halfH;

  let surface = {
    x: clamp(from.x, left, right),
    y: clamp(from.y, top, bottom),
  };
  let dx = from.x - surface.x;
  let dy = from.y - surface.y;
  let length = Math.hypot(dx, dy);

  // This only occurs if a squad was trapped inside a newly stamped footprint. Pick the
  // nearest side deterministically; movement's final walkability pass will eject it too.
  if (length < 1e-9) {
    const localX = from.x - building.pos.x;
    const localY = from.y - building.pos.y;
    const toVertical = halfW - Math.abs(localX);
    const toHorizontal = halfH - Math.abs(localY);
    if (toVertical <= toHorizontal) {
      const sign = localX < 0 ? -1 : 1;
      surface = { x: building.pos.x + sign * halfW, y: clamp(from.y, top, bottom) };
      dx = sign;
      dy = 0;
    } else {
      const sign = localY < 0 ? -1 : 1;
      surface = { x: clamp(from.x, left, right), y: building.pos.y + sign * halfH };
      dx = 0;
      dy = sign;
    }
    length = 1;
  }

  // Aim slightly inside weapon range so grid quantization cannot leave the squad holding
  // a few centimeters outside the combat system's range check.
  const standOff = Math.max(0.55, weaponRange - 0.35);
  return {
    x: surface.x + (dx / length) * standOff,
    y: surface.y + (dy / length) * standOff,
  };
}

/** Near-surface point used to prove a structure is in the same navigable region. */
export function buildingAccessPoint(from: Vec2, building: Building): Vec2 {
  return buildingApproachPoint(from, building, 0);
}
